/* eslint-disable no-var */
// Money in whatever currency a store charges.
//
// The backend prices India in rupees and, by default, every other store in
// dollars; a store switched to its own currency (backend LOCAL_CURRENCY_STORES)
// prices everything in that currency instead: euros in Germany, pounds in the
// UK, yen in Japan, dinars in Kuwait. Every price the API returns names its
// currency (a plan's `currency_type`, the quote's `currency`), so the site
// shows and charges exactly that and never assumes rupees-or-dollars.
//
// One formatter for every surface: the purchase screens (utils/format.js),
// the brand, category and store pages and their prerender (src/seo).
//
// CommonJS on purpose, like src/seo/brandPage.js: Node requires it at build
// time, and webpack and Jest both accept it from the app.

// As the backend's marketplace.market.SYMBOLS. A currency not named here
// shows its code and a space: "CHF 12", "SEK 99". Intl's narrowSymbol is not
// used: it shows CAD and AUD as a bare "$".
var SYMBOLS = {
  inr: '₹',
  usd: '$',
  eur: '€',
  gbp: '£',
  aud: 'A$',
  cad: 'CA$',
  nzd: 'NZ$',
  sgd: 'S$',
  hkd: 'HK$',
  mxn: 'MX$',
  brl: 'R$',
  jpy: '¥',
  ils: '₪',
  try: '₺',
  thb: '฿',
  php: '₱',
  krw: '₩',
};

// Spellings the catalogue has carried for a real code.
var ALIASES = { gpb: 'gbp' };

// Currencies priced in whole units, and those with three decimals (ISO 4217).
var ZERO_DECIMAL = ['jpy', 'krw', 'vnd', 'clp', 'huf', 'twd', 'isk'];
var THREE_DECIMAL = ['bhd', 'jod', 'kwd', 'omr', 'tnd', 'lyd'];

/**
 * A currency as the API spells it, lower case: "eur", "usd". Blank is
 * rupees, as the backend treats a plan with no currency.
 */
var currencyType = function (type) {
  var t = String(type == null ? '' : type).trim().toLowerCase();
  if (!t) return 'inr';
  return ALIASES[t] || t;
};

/** Whether this is a rupee price (India's store). */
var isInr = function (type) {
  return currencyType(type) === 'inr';
};

/** The ISO code: "INR", "USD", "EUR". For payments and structured data. */
var currencyCode = function (type) {
  return currencyType(type).toUpperCase();
};

/** "₹", "$", "€", or "CHF " (a code and a space) for one without a symbol. */
var currencySymbol = function (type) {
  var t = currencyType(type);
  return SYMBOLS[t] || t.toUpperCase() + ' ';
};

/** Decimal places the currency is charged in: 0 for yen, 3 for dinars, else 2. */
var minorDigits = function (type) {
  var t = currencyType(type);
  if (ZERO_DECIMAL.indexOf(t) !== -1) return 0;
  if (THREE_DECIMAL.indexOf(t) !== -1) return 3;
  return 2;
};

/** An amount rounded to what the currency can be charged in ($9.99, ¥1,499). */
var roundMinor = function (n, type) {
  var scale = Math.pow(10, minorDigits(type));
  return Math.round(Number(n) * scale) / scale;
};

/**
 * "₹1,25,000", "$9.99", "$12", "€9.50", "¥1,499", "CHF 12.50", "KWD 2.250".
 *
 * Rupees round to whole numbers with Indian grouping. Every other currency
 * keeps its decimals only when there are any ("$12", not "$12.00"); yen, won
 * and the like are always whole.
 */
var formatMoney = function (n, type) {
  var v = Number(n);
  if (!Number.isFinite(v)) return '';
  var t = currencyType(type);
  if (t === 'inr') return '₹' + Math.round(v).toLocaleString('en-IN');
  var r = roundMinor(v, t);
  var digits = Number.isInteger(r) ? 0 : minorDigits(t);
  return currencySymbol(t) + r.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

var escapeRe = function (s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Symbol to currency, "$" being dollars and "¥" yen.
var BY_SYMBOL = Object.keys(SYMBOLS).reduce(function (acc, code) {
  acc[SYMBOLS[code]] = code;
  return acc;
}, {});

// Longest symbol first, so "CA$" is never read as "$"; then any 3-letter code.
var PREFIX = Object.keys(BY_SYMBOL)
  .sort(function (a, b) {
    return b.length - a.length;
  })
  .map(escapeRe)
  .concat('[A-Z]{3}')
  .join('|');
var LABEL = new RegExp('^\\s*(' + PREFIX + ')\\s?([0-9][0-9,]*(?:\\.[0-9]+)?)(.*)$');

/**
 * A price label the API formats ("$175.99/12 mo", "€9/mo", "CHF 12/mo",
 * "₹649/6 mo") read back: { currency, amount, rest }, where rest is whatever
 * follows the number ("/12 mo"). Null when it isn't one.
 */
var parseMoneyLabel = function (label) {
  var m = LABEL.exec(String(label == null ? '' : label));
  if (!m) return null;
  var amount = Number(m[2].replace(/,/g, ''));
  if (!Number.isFinite(amount)) return null;
  return { currency: BY_SYMBOL[m[1]] || currencyType(m[1]), amount: amount, rest: m[3] };
};

module.exports = {
  SYMBOLS: SYMBOLS,
  currencyType: currencyType,
  isInr: isInr,
  currencyCode: currencyCode,
  currencySymbol: currencySymbol,
  minorDigits: minorDigits,
  roundMinor: roundMinor,
  formatMoney: formatMoney,
  parseMoneyLabel: parseMoneyLabel,
};
